import { Color3, GizmoManager, Mesh, MeshBuilder, Observer, PBRMetallicRoughnessMaterial, PointerEventTypes, Scene, Texture, Vector3 } from "@babylonjs/core";
import { Point3 } from "../models/point.type";

export class ImageTileService {

    private imageTile: Mesh;
    private planeMaterial: PBRMetallicRoughnessMaterial;
    private gizmoManager: GizmoManager;

    private readonly defaultImageWidth = 300;
    private scale: number;
    private pointerObservable: Observer<any>;

    positionRotationScaleChanged: (res: { position: Point3, rotation: Point3, scale: number }) => void;
    pointSelectedCallback: (pt: Point3) => void;

    twoPoints: [Mesh?, Mesh?] = [];

    private scene: Scene;

    constructor(scene: Scene) {
        this.scene = scene;
    }

    startEdit() {
        if(this.gizmoManager){
            this.gizmoManager.positionGizmoEnabled = true;
            this.gizmoManager.rotationGizmoEnabled = true;
    
            this.gizmoManager.enableAutoPicking = true;
            this.gizmoManager.clearGizmoOnEmptyPointerEvent = true;
        }
    }

    stopEdit() {
        if(this.gizmoManager){
            this.gizmoManager.positionGizmoEnabled = false;
            this.gizmoManager.rotationGizmoEnabled = false;
    
            this.gizmoManager.enableAutoPicking = false;
            this.gizmoManager.clearGizmoOnEmptyPointerEvent = false;
        }
    }

    applyScaleToImageTile(scale: number) {
        if (this.imageTile) {
            this.imageTile.scaling.x = scale;
            this.imageTile.scaling.y = scale;
        }
    }

    computeScale(pointOne: Vector3, pointTwo: Vector3, distanceInReality: number): number {
        const actualDistance = Vector3.Distance(pointOne, pointTwo);
        const scale = distanceInReality / actualDistance;
        return scale;
    }

    setImageFileTile(image: File, position: Point3, rotation: Point3, width: number, height: number, scale: number) {
        if (this.imageTile) {
            this.imageTile.dispose();
        }
        const blobURL = URL.createObjectURL(image);
        this.setImageUrlTile(blobURL, position, rotation, width, height, scale);
    }

    computeDefaultScale(width: number) {
        return this.defaultImageWidth / width;
    }

    computeScaleOnDimensionAndTwoPoints(first: Point3, second: Point3, distance: number, oldScale: number) {
        const vec1 = new Vector3(first[0], first[1], first[2]);
        const vec2 = new Vector3(second[0], second[1], second[2]);
        const distBetweenPoints = Vector3.Distance(vec1, vec2);
        return distance / distBetweenPoints * oldScale;
    }

    applyScaleOnImageTile(width: number, height: number, position: Point3, rotation: Point3, scale: number) {
        let computedWidth = width * scale;
        let computedHeight = height * scale;
        this.imageTile.dispose();
        this.createImageTile(computedWidth, computedHeight, position, rotation);
        this.setupGizmo();
    }

    deleteImageTile() {
        if(this.imageTile){
            this.imageTile.dispose();
        }
        if(this.planeMaterial){
            this.planeMaterial.dispose();
        }
    }

    showImageTile() {
        this.imageTile.isVisible = true;
    }

    hideImageTile() {
        this.imageTile.isVisible = false;
    }

    setImageUrlTile(blobURL: string, position: Point3, rotation: Point3, width: number, height: number, scale: number) {
        this.scale = scale;
        this.planeMaterial = new PBRMetallicRoughnessMaterial("texturePlane", this.scene);
        const texture = new Texture(blobURL, this.scene);
        this.planeMaterial.baseTexture = texture;
        this.planeMaterial.metallic = 0;
        this.planeMaterial.roughness = 1;
        this.planeMaterial.baseTexture.wrapU = Texture.CLAMP_ADDRESSMODE;
        this.planeMaterial.baseTexture.wrapV = Texture.CLAMP_ADDRESSMODE;

        let computedWidth;
        let computedHeight;

        computedWidth = width * scale;
        computedHeight = height * scale;

        this.createImageTile(computedWidth, computedHeight, position, rotation);

        this.setupGizmo();
    }

    updatePoints(pt) {
        if (this.twoPoints.length === 0 || this.twoPoints.length === 2) {
            this.twoPoints.forEach(p => {
                p.dispose(false, true);
            })
            const pt1 = MeshBuilder.CreateSphere('pt1', { diameter: 2 }, this.scene);
            pt1.position = pt;
            const material = new PBRMetallicRoughnessMaterial('matpt1', this.scene);
            material.baseColor = new Color3(0, 0, 1);
            pt1.material = material;
            this.twoPoints = [pt1];
        } else {
            const pt2 = MeshBuilder.CreateSphere('pt2', { diameter: 2 }, this.scene);
            pt2.position = pt;
            const material = new PBRMetallicRoughnessMaterial('matpt2', this.scene);
            material.baseColor = new Color3(0, 0, 1);
            pt2.material = material;
            this.twoPoints.push(pt2);
        }
    }

    selectingTwoPoints(selecting) {
        if (selecting) {
            this.pointerObservable = this.scene.onPointerObservable.add((pt) => {
                if (pt.type === PointerEventTypes.POINTERUP) {
                    if (pt.pickInfo.pickedMesh === this.imageTile) {
                        const point = pt.pickInfo.pickedPoint;
                        this.pointSelectedCallback([point.x, point.y, point.z])
                        this.updatePoints(point);
                    }
                }
            })
        } else if (this.pointerObservable) {
            this.pointerObservable.unregisterOnNextCall = true;
            this.twoPoints.forEach(p => {
                p.dispose(false, true);
            })
        }
    }

    private createImageTile(computedWidth: any, computedHeight: any, position: Point3, rotation: Point3) {
        this.imageTile = MeshBuilder.CreatePlane("plane", { width: computedWidth, height: computedHeight }, this.scene);
        this.imageTile.position = new Vector3(position[0], position[1], position[2]);
        this.imageTile.rotation = new Vector3(rotation[0], rotation[1], rotation[2]);
        this.imageTile.receiveShadows = true;
        this.imageTile.material = this.planeMaterial;
        this.imageTile.scaling.x = -1;
    }

    private setupGizmo() {
        this.gizmoManager = new GizmoManager(this.scene);
        this.gizmoManager.positionGizmoEnabled = true;
        this.gizmoManager.rotationGizmoEnabled = true;

        this.gizmoManager.attachableMeshes = [this.imageTile];
        this.gizmoManager.enableAutoPicking = false;
        this.gizmoManager.clearGizmoOnEmptyPointerEvent = false;

        if (this.gizmoManager.gizmos.positionGizmo) {
            this.gizmoManager.gizmos.positionGizmo.zPlaneGizmo.isEnabled = true;
            this.gizmoManager.gizmos.positionGizmo.zGizmo.isEnabled = false;

            this.gizmoManager.gizmos.positionGizmo.onDragEndObservable.add(() => {
                if (this.gizmoManager.attachableMeshes && this.gizmoManager.attachableMeshes.length > 0) {
                    const atMesh = this.gizmoManager.attachableMeshes[0];
                    const eulers = atMesh.absoluteRotationQuaternion.toEulerAngles();
                    const pos = atMesh.position;
                    this.positionRotationScaleChanged({ position: [pos.x, pos.y, pos.z], rotation: [eulers.x, eulers.y, eulers.z], scale: this.scale });
                }
            });
        }
        if (this.gizmoManager.gizmos.rotationGizmo) {
            this.gizmoManager.gizmos.rotationGizmo.xGizmo.isEnabled = false;
            this.gizmoManager.gizmos.rotationGizmo.zGizmo.isEnabled = true;
            this.gizmoManager.gizmos.rotationGizmo.yGizmo.isEnabled = false;
            this.gizmoManager.gizmos.rotationGizmo.onDragEndObservable.add(() => {
                if (this.gizmoManager.attachableMeshes && this.gizmoManager.attachableMeshes.length > 0) {
                    const atMesh = this.gizmoManager.attachableMeshes[0];
                    const eulers = atMesh.absoluteRotationQuaternion.toEulerAngles();
                    const pos = atMesh.position;
                    this.positionRotationScaleChanged({ position: [pos.x, pos.y, pos.z], rotation: [eulers.x, eulers.y, eulers.z], scale: this.scale });
                }
            });
        }
    }
}